import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { Plus, Mail, Trash2, Edit, ArrowLeft, Coins, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewsletterList = ({ user }) => {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState([]);
  const [quota, setQuota] = useState({ total: 0, used: 0, remaining: 0 });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/newsletter/drafts?user_id=${user.user_id}`);
      setDrafts(resp.data.drafts || []);
      setQuota(resp.data.quota || { total: 0, used: 0, remaining: 0 });
    } catch (e) {
      toast.error('Σφάλμα φόρτωσης newsletters');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  const handleCreate = async () => {
    if (quota.remaining <= 0) {
      toast.error('Δεν έχετε διαθέσιμα credits. Επικοινωνήστε με τον διαχειριστή.');
      return;
    }
    setCreating(true);
    try {
      const resp = await axios.post(`${API}/newsletter/drafts?user_id=${user.user_id}`, {
        title: `Newsletter ${new Date().toLocaleDateString('el-GR')}`
      });
      const newId = resp.data.newsletter.newsletter_id;
      navigate(`/newsletter/${newId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Σφάλμα δημιουργίας');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Σίγουρα θέλετε να διαγράψετε αυτό το newsletter; (Δεν επιστρέφονται credits)')) return;
    try {
      await axios.delete(`${API}/newsletter/drafts/${id}?user_id=${user.user_id}`);
      toast.success('Διαγράφηκε');
      fetchData();
    } catch (e) {
      toast.error('Σφάλμα διαγραφής');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  const statusBadge = (status) => {
    const map = {
      draft: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Πρόχειρο' },
      sent: { bg: 'bg-green-100', text: 'text-green-700', label: 'Στάλθηκε' },
      sending: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Σε αποστολή' },
      failed: { bg: 'bg-red-100', text: 'text-red-700', label: 'Αποτυχία' },
    };
    const s = map[status] || map.draft;
    return <span className={`text-xs px-2 py-0.5 rounded ${s.bg} ${s.text}`}>{s.label}</span>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> Πίσω
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Mail className="h-6 w-6 text-pink-600" /> Newsletter Manager
              </h1>
              <p className="text-sm text-slate-600">Δημιουργήστε επαγγελματικά newsletters με AI</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-white border rounded-lg px-4 py-2 flex items-center gap-2 shadow-sm">
              <Coins className="h-4 w-4 text-amber-500" />
              <div>
                <div className="text-xs text-slate-500">Credits</div>
                <div className="font-bold text-slate-900" data-testid="quota-display">
                  {quota.remaining} <span className="text-xs text-slate-400">/ {quota.total}</span>
                </div>
              </div>
            </div>
            <Button onClick={handleCreate} disabled={creating || quota.remaining <= 0} className="bg-pink-600 hover:bg-pink-700" data-testid="create-newsletter-btn">
              <Plus className="h-4 w-4 mr-1" /> Νέο Newsletter
            </Button>
          </div>
        </div>

        {/* Quota warning */}
        {quota.remaining <= 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-amber-800">
              ⚠️ Δεν έχετε διαθέσιμα credits. Επικοινωνήστε με τον διαχειριστή για να σας προσθέσει.
            </p>
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="text-center py-12 text-slate-500">Φόρτωση...</div>
        ) : drafts.length === 0 ? (
          <Card className="p-12 text-center">
            <Mail className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-600 mb-4">Δεν έχετε δημιουργήσει ακόμα κανένα newsletter.</p>
            <Button onClick={handleCreate} disabled={quota.remaining <= 0} className="bg-pink-600 hover:bg-pink-700">
              <Plus className="h-4 w-4 mr-1" /> Δημιουργήστε το πρώτο σας
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {drafts.map(nl => (
              <Card
                key={nl.newsletter_id}
                onClick={() => navigate(`/newsletter/${nl.newsletter_id}`)}
                className="p-4 cursor-pointer hover:shadow-md transition-shadow"
                data-testid={`newsletter-card-${nl.newsletter_id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900 truncate">{nl.title}</h3>
                    {nl.subject && <p className="text-sm text-slate-600 truncate mt-1">{nl.subject}</p>}
                  </div>
                  {statusBadge(nl.status)}
                </div>
                <div className="flex items-center justify-between text-xs text-slate-500 mt-3 pt-3 border-t">
                  <span>Ενημέρωση: {formatDate(nl.updated_at)}</span>
                  <div className="flex gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/newsletter/${nl.newsletter_id}`); }}
                      className="p-1 hover:bg-slate-100 rounded"
                      title="Επεξεργασία"
                    >
                      <Edit className="h-3.5 w-3.5 text-slate-600" />
                    </button>
                    {nl.status !== 'sent' && (
                      <button
                        onClick={(e) => handleDelete(nl.newsletter_id, e)}
                        className="p-1 hover:bg-red-50 rounded"
                        title="Διαγραφή"
                        data-testid={`delete-${nl.newsletter_id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-red-600" />
                      </button>
                    )}
                  </div>
                </div>
                {nl.sent_count > 0 && (
                  <div className="mt-2 text-xs text-green-700 flex items-center gap-1">
                    <Send className="h-3 w-3" /> Στάλθηκε σε {nl.sent_count} παραλήπτες
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsletterList;
